import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Avatar, Stack, IconButton, Alert, CircularProgress, TextField, Chip } from '@mui/material';
import { Edit, Person, Email, Phone, AdminPanelSettings, Lock, CloudUpload } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { get, put, del, post } from '../common/axiosClient';
import { toast } from 'react-toastify';
import { Typography, Button } from 'components/index';
import ChangePassword from '../components/user-management/ChangePassword';
import { validateName } from '../common/utils/common.util';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  signatureUrl?: string;
  roles?: string[];
}

interface EditableProfile {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  signatureUrl?: string;
  signatureFile?: File | null;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const UserProfilePage: React.FC = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  // The report wizards read the author (name, email, signature) from the cached 'currentUserProfile'
  // query, so every profile write has to invalidate it or reports keep using the previous values.
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [editableProfile, setEditableProfile] = useState<EditableProfile>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    signatureUrl: undefined,
    signatureFile: null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [isChangePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Check if user is admin
  const isAdmin = hasRole('Admin') || hasRole('System Admin');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const profileResponse = await get('/user/profile');
        const fullProfile: UserProfile = {
          ...profileResponse,
          roles: user?.roles || [],
        };
        setProfile(fullProfile);
        setEditableProfile({
          firstName: fullProfile.firstName || '',
          lastName: fullProfile.lastName || '',
          email: fullProfile.email || '',
          phoneNumber: fullProfile.phoneNumber || '',
          signatureUrl: fullProfile.signatureUrl,
          signatureFile: null,
        });
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError(err.message || 'Failed to fetch profile');
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfile();
    }
  }, [user]);

  const handleFirstNameChange = (value: string) => {
    setEditableProfile({ ...editableProfile, firstName: value });
    const error = validateName(value, true);
    setFirstNameError(error);
  };

  const handleLastNameChange = (value: string) => {
    setEditableProfile({ ...editableProfile, lastName: value });
    // LastName is optional, so isRequired is false
    const error = validateName(value, false);
    setLastNameError(error);
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    // Clear validation errors when entering edit mode
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
  };

  const handleSaveProfile = async () => {
    // Validate before saving
    const firstNameErr = validateName(editableProfile.firstName, true);
    const lastNameErr = validateName(editableProfile.lastName, false);

    setFirstNameError(firstNameErr);
    setLastNameError(lastNameErr);

    if (firstNameErr || lastNameErr) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    try {
      setIsSaving(true);
      // Update the profile
      await put('/user/profile', {
        firstName: editableProfile.firstName.trim(),
        lastName: editableProfile.lastName?.trim() || null,
        email: editableProfile.email,
        phoneNumber: editableProfile.phoneNumber,
      });

      setIsEditing(false);
      toast.success('Profile updated successfully');

      // Refresh profile data
      const profileResponse = await get('/user/profile');
      setProfile({
        ...profileResponse,
        roles: user?.roles || [],
      });
      await queryClient.invalidateQueries('currentUserProfile');
    } catch (err: any) {
      console.error('Error updating profile:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSignature = async () => {
    try {
      console.log('Deleting signature...');
      console.log('Making DELETE request to /user/signature');

      const result = await del('/user/signature');
      console.log('Delete result:', result);

      // Refresh profile data from server
      console.log('Fetching updated profile...');
      const profileResponse = await get('/user/profile');
      console.log('Updated profile:', profileResponse);

      setProfile({
        ...profileResponse,
        roles: user?.roles || [],
      });
      setEditableProfile(prev => ({
        ...prev,
        signatureUrl: undefined,
        signatureFile: null,
      }));
      await queryClient.invalidateQueries('currentUserProfile');

      toast.success('Signature deleted successfully');
    } catch (err: any) {
      console.error('Full error object:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      toast.error(`Failed to delete signature: ${err.message}`);
    }
  };

  const handleUploadSignature = async (file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/heic'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      toast.error('Invalid file type. Only JPG, PNG, and HEIC formats are allowed.');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size should not exceed 2MB');
      return;
    }

    try {
      setIsUploadingSignature(true);
      const formData = new FormData();
      formData.append('file', file);
      await post('/user/signature', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refresh profile data to get the new signature URL
      const profileResponse = await get('/user/profile');
      setProfile({
        ...profileResponse,
        roles: user?.roles || [],
      });
      setEditableProfile(prev => ({
        ...prev,
        signatureUrl: profileResponse.signatureUrl,
        signatureFile: null,
      }));
      await queryClient.invalidateQueries('currentUserProfile');

      toast.success('Signature uploaded successfully');
    } catch (err: any) {
      console.error('Error uploading signature:', err);
      toast.error('Failed to upload signature');
    } finally {
      setIsUploadingSignature(false);
    }
  };

  const handleCancelEdit = () => {
    if (profile) {
      setEditableProfile({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        signatureUrl: profile.signatureUrl,
        signatureFile: null,
      });
    }
    // Clear validation errors when canceling
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setIsEditing(false);
  };

  const handleManageUsers = () => {
    navigate('/users');
  };

  const handleOpenChangePassword = () => setChangePasswordDialogOpen(true);
  const handleCloseChangePassword = () => setChangePasswordDialogOpen(false);

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='60vh'>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography>Please try refreshing the page or contact support if the problem persists.</Typography>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity='warning'>No profile information available.</Alert>
      </Box>
    );
  }

  const fullName = `${profile.firstName}${profile.lastName ? ` ${profile.lastName}` : ''}`.trim();
  const initials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase();

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mx: 0,
        mt: { xs: 1, md: 2 },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {/* Header Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction='row' spacing={3} alignItems='center'>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: 'primary.main',
                fontSize: '3rem',
                fontWeight: 'bold',
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography variant='h4' component='h1' gutterBottom sx={{ textAlign: 'left' }}>
                {fullName}
              </Typography>
              <Typography variant='h6' fontWeight='light' gutterBottom>
                {profile.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {profile.roles?.map((role, index) => (
                  <Chip
                    key={index}
                    label={role}
                    size='small'
                    color={role === 'System Admin' ? 'error' : role === 'CDCC Admin' ? 'warning' : 'primary'}
                    variant='outlined'
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Box sx={{ flex: 1 }}>
        {/* Two Column Layout */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 3, mb: 3 }}>
          {/* Personal Information */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant='h5'>Personal Information</Typography>
                {isEditing ? (
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Button
                      variant='contained'
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      startIcon={isSaving ? <CircularProgress size={20} color='inherit' /> : null}
                    >
                      Save Changes
                    </Button>
                    <Button variant='outlined' onClick={handleCancelEdit} disabled={isSaving}>
                      Cancel
                    </Button>
                  </Stack>
                ) : (
                  <IconButton onClick={handleEditProfile}>
                    <Edit />
                  </IconButton>
                )}
              </Box>
              <Stack spacing={2}>
                <Box display='flex' alignItems='flex-start' gap={1}>
                  <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', mt: '3px' }}>
                    <Person color='primary' />
                  </Box>
                  <Box flex={1}>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      First Name
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size='small'
                        value={editableProfile.firstName}
                        onChange={e => handleFirstNameChange(e.target.value)}
                        error={!!firstNameError}
                        helperText={firstNameError}
                        required
                      />
                    ) : (
                      <Typography variant='h6' fontWeight='medium'>
                        {profile.firstName}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box display='flex' alignItems='flex-start' gap={1}>
                  <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', mt: '3px' }}>
                    <Person color='primary' />
                  </Box>
                  <Box flex={1}>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Last Name
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size='small'
                        value={editableProfile.lastName}
                        onChange={e => handleLastNameChange(e.target.value)}
                        error={!!lastNameError}
                        helperText={lastNameError}
                      />
                    ) : (
                      <Typography variant='h6' fontWeight='medium'>
                        {profile.lastName || ''}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box display='flex' alignItems='flex-start' gap={1}>
                  <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', mt: '3px' }}>
                    <Email color='primary' />
                  </Box>
                  <Box flex={1}>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Email Address
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size='small'
                        value={editableProfile.email}
                        onChange={e => setEditableProfile({ ...editableProfile, email: e.target.value })}
                        error={!!emailError}
                        helperText={emailError}
                      />
                    ) : (
                      <Typography variant='h6' fontWeight='medium'>
                        {profile.email}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box display='flex' alignItems='flex-start' gap={1}>
                  <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', mt: '2px' }}>
                    <Phone color='primary' />
                  </Box>
                  <Box flex={1}>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Phone Number
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size='small'
                        value={editableProfile.phoneNumber}
                        onChange={e => setEditableProfile({ ...editableProfile, phoneNumber: e.target.value })}
                        placeholder='Enter phone number'
                      />
                    ) : (
                      <Typography variant='h6' fontWeight='medium'>
                        {profile.phoneNumber || 'No phone number provided'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Signature Card */}
          <Card sx={{ position: 'relative' }}>
            <CardContent sx={{ p: 0, height: '400px', display: 'flex', flexDirection: 'column' }}>
              <Typography variant='h5' sx={{ p: 3, pb: 2 }}>
                Signature
              </Typography>
              {profile.signatureUrl &&
              profile.signatureUrl !== 'null' &&
              profile.signatureUrl !== '' &&
              profile.signatureUrl !== 'undefined' ? (
                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    overflow: 'hidden',
                    '&:hover .signature-overlay': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      minHeight: 300,
                      p: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component='img'
                      src={profile.signatureUrl}
                      alt='User Signature'
                      onError={e => {
                        console.error('Image load error for signature');
                        console.error('Failed URL:', (e.target as HTMLImageElement).src);
                        console.error('Original signature URL:', profile.signatureUrl);
                      }}
                      onLoad={() => console.log('Signature image loaded successfully')}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </Box>

                  {/* Overlay with Upload and Delete buttons */}
                  <Box
                    className='signature-overlay'
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      height: '40%',
                      width: '100%',
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0))',
                      color: 'white',
                      py: 2,
                      px: 2,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-end',
                      alignItems: 'end',
                      transition: 'opacity 0.3s ease-in-out',
                      opacity: 0,
                    }}
                  >
                    <Button variant='standard' color='error' onClick={handleDeleteSignature} sx={{ mr: 1 }} disabled={isUploadingSignature}>
                      Delete
                    </Button>
                    <Button variant='standard' component='label' disabled={isUploadingSignature}>
                      Replace
                      <input
                        type='file'
                        hidden
                        accept='image/jpeg,image/png,image/heic'
                        onChange={async e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            await handleUploadSignature(file);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                      />
                    </Button>
                  </Box>
                  {isUploadingSignature && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        zIndex: 3,
                      }}
                    >
                      <CircularProgress color='inherit' />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  component='label'
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 3,
                    cursor: 'pointer',
                    flex: 1,
                    m: 3,
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant='h6' color='text.secondary' gutterBottom>
                    UPLOAD SIGNATURE
                  </Typography>
                  <Typography variant='caption' color='text.disabled'>
                    Accepted formats: JPG, PNG, HEIC. Max size: 2MB
                  </Typography>

                  <input
                    type='file'
                    hidden
                    accept='image/jpeg,image/png,image/heic'
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        await handleUploadSignature(file);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Security Card */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock color='primary' />
                <Typography variant='h5'>Security</Typography>
              </Box>
              <Stack direction='row' spacing={2}>
                <Button variant='outlined' startIcon={<Lock />} onClick={handleOpenChangePassword}>
                  Change Password
                </Button>
                {isAdmin && (
                  <Button variant='contained' startIcon={<AdminPanelSettings />} onClick={handleManageUsers}>
                    Manage Users
                  </Button>
                )}
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <ChangePassword open={isChangePasswordDialogOpen} onClose={handleCloseChangePassword} />
    </Box>
  );
};

export default UserProfilePage;
